import React from "react";
import { Navigate } from "react-router-dom";
import { useUnit } from "effector-react";
import { $canEditComplex, $isAuthenticated, $authLoading, $user } from "../store/auth";
import LoginPage from "../pages/LoginPage";
import Home from "../pages/Home";
import AddClient from "../pages/AddClient";
import AddResidentialComplex from "../pages/AddResidentialComplex";
import LoadingScreen from "../components/LoadingScreen";
import { userHasRole } from "../config/roles";
import ComplexDetails from "../pages/ComplexDetails";
import TermsPage from "../pages/TermsPage";
import PrivacyPolicyPage from "../pages/PrivacyPolicyPage";
import AppHeader from "../components/AppHeader";
import BuildingCorpsPage from "../pages/BuildingCorpsPage";
import ManualSubmissionProgressPage from "../pages/ManualSubmissionProgressPage";
import CorpsDevicesPage from "../pages/CorpsDevicesPage";
import ContractorsPage from "../pages/ContractorsPage";
import FieldStaffPage from "../pages/FieldStaffPage";
import MeterReadingsImportHistoryPage from "../pages/MeterReadingsImportHistoryPage";
import ReadingsProgressPage from "../pages/ReadingsProgressPage";
import GatewaysPage from "../pages/GatewaysPage";
import OnboardingPage from "../pages/OnboardingPage";
import MobileNewsPage from "../pages/MobileNewsPage";
import MobileRegistrationsPage from "../pages/MobileRegistrationsPage";
import CalculationsPage from "../pages/CalculationsPage";
import PremisesPage from "../pages/PremisesPage";
import SettlementElectricityPage from "../pages/SettlementElectricityPage";
import ApartmentDetailsPage from "../pages/ApartmentDetailsPage";
import TicketsPage from "../pages/TicketsPage";
import IntegrationsPage from "../pages/IntegrationsPage";
import LoRaWanDiagnosticsPage from "../pages/LoRaWanDiagnosticsPage";
import LoRaWanSettingsPage from "../pages/LoRaWanSettingsPage";
import EdgeAgentsPage from "../pages/EdgeAgentsPage";

// Protected Route component
export const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, isLoading] = useUnit([
    $isAuthenticated,
    $authLoading,
  ]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Role-based Route guard
export const RoleRoute = ({ children, allow }) => {
  const [isLoading, user] = useUnit([$authLoading, $user]);
  if (isLoading) {
    return <LoadingScreen />;
  }
  const allowed = Array.isArray(allow)
    ? allow.some((r) => userHasRole(user, r))
    : userHasRole(user, allow);
  return allowed ? children : <Navigate to="/home" replace />;
};

export const TenantWriteRoute = ({ children }) => {
  const [isLoading, canEdit] = useUnit([$authLoading, $canEditComplex]);
  if (isLoading) {
    return <LoadingScreen />;
  }
  return canEdit ? children : <Navigate to="/home" replace />;
};

// Public Route component (for routes that shouldn't be accessible when authenticated, like login)
export const PublicRoute = ({ children }) => {
  const [isAuthenticated, isLoading] = useUnit([
    $isAuthenticated,
    $authLoading,
  ]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return !isAuthenticated ? children : <Navigate to="/home" replace />;
};

// Main routes configuration
export const routes = [
  {
    path: "/",
    element: <Navigate to="/home" replace />,
  },
  {
    path: "/home",
    element: (
      <ProtectedRoute>
        <AppHeader
          title="Главная"
          onGoToUsers={() => {}}
          onLogout={() => {}}
        />
        <Home />
      </ProtectedRoute>
    ),
  },
  {
    path: "/corps/:corpsId",
    element: (
      <ProtectedRoute>
        <AppHeader
          title="Устройства корпуса"
          onGoToUsers={() => {}}
          onLogout={() => {}}
        />
        <CorpsDevicesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/meter-readings/imports",
    element: (
      <ProtectedRoute>
        <RoleRoute allow={["superadmin", "admin", "contractor"]}>
          <AppHeader title="Импорт показаний" onGoToUsers={() => {}} onLogout={() => {}} />
          <MeterReadingsImportHistoryPage />
        </RoleRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/gateways",
    element: (
      <ProtectedRoute>
        <AppHeader title="Шлюзы" onGoToUsers={() => {}} onLogout={() => {}} />
        <GatewaysPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/edge-agents",
    element: (
      <ProtectedRoute>
        <RoleRoute allow={["superadmin", "admin", "manager", "contractor"]}>
          <AppHeader title="Агенты" onGoToUsers={() => {}} onLogout={() => {}} />
          <EdgeAgentsPage />
        </RoleRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/calculations",
    element: (
      <ProtectedRoute>
        <RoleRoute allow={["superadmin", "admin", "manager", "contractor"]}>
          <AppHeader title="Расчёты" onGoToUsers={() => {}} onLogout={() => {}} />
          <CalculationsPage />
        </RoleRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/meter-readings/progress",
    element: (
      <ProtectedRoute>
        <RoleRoute allow={["superadmin", "admin", "contractor"]}>
          <AppHeader title="Прогресс сбора показаний" onGoToUsers={() => {}} onLogout={() => {}} />
          <ReadingsProgressPage />
        </RoleRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/premises",
    element: (
      <ProtectedRoute>
        <RoleRoute allow={["superadmin", "admin", "manager", "contractor"]}>
          <AppHeader onGoToUsers={() => {}} onLogout={() => {}} />
          <PremisesPage />
        </RoleRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/settlement/electricity",
    element: (
      <ProtectedRoute>
        <RoleRoute allow={["superadmin", "admin", "manager", "contractor"]}>
          <AppHeader title="Электроэнергия" onGoToUsers={() => {}} onLogout={() => {}} />
          <SettlementElectricityPage />
        </RoleRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/apartments/:apartmentId",
    element: (
      <ProtectedRoute>
        <RoleRoute allow={["superadmin", "admin", "manager", "contractor"]}>
          <AppHeader title="Квартира" onGoToUsers={() => {}} onLogout={() => {}} />
          <ApartmentDetailsPage />
        </RoleRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/tickets",
    element: (
      <ProtectedRoute>
        <RoleRoute allow={["superadmin", "admin", "manager", "contractor"]}>
          <AppHeader title="Заявки" onGoToUsers={() => {}} onLogout={() => {}} />
          <TicketsPage />
        </RoleRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/tickets/:ticketId",
    element: (
      <ProtectedRoute>
        <RoleRoute allow={["superadmin", "admin", "manager", "contractor"]}>
          <AppHeader title="Карточка заявки" onGoToUsers={() => {}} onLogout={() => {}} />
          <TicketsPage />
        </RoleRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/complex/:id",
    element: (
      <ProtectedRoute>
        <AppHeader
          title="Жилой комплекс"
          onGoToUsers={() => {}}
          onLogout={() => {}}
        />
        <ComplexDetails />
      </ProtectedRoute>
    ),
  },
  {
    path: "/building/:buildingId/corps",
    element: (
      <ProtectedRoute>
        <AppHeader title="Здания" onGoToUsers={() => {}} onLogout={() => {}} />
        <BuildingCorpsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/building/:buildingId/manual-progress",
    element: (
      <ProtectedRoute>
        <AppHeader
          title="Прогресс передачи показаний"
          onGoToUsers={() => {}}
          onLogout={() => {}}
        />
        <ManualSubmissionProgressPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/add-client",
    element: (
      <ProtectedRoute>
        <RoleRoute allow={["superadmin"]}>
          <AddClient />
        </RoleRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/contractors",
    element: (
      <ProtectedRoute>
        <RoleRoute allow={["superadmin"]}>
          <ContractorsPage />
        </RoleRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/add-complex",
    element: (
      <ProtectedRoute>
        <TenantWriteRoute>
          <AppHeader title="Добавление комплекса" onGoToUsers={() => {}} onLogout={() => {}} />
          <AddResidentialComplex />
        </TenantWriteRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/bulk-import",
    element: (
      <ProtectedRoute>
        <TenantWriteRoute>
          <Navigate to="/admin/add-complex?mode=import" replace />
        </TenantWriteRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/onboarding",
    element: (
      <ProtectedRoute>
        <TenantWriteRoute>
          <AppHeader title="Быстрый старт" onGoToUsers={() => {}} onLogout={() => {}} />
          <OnboardingPage />
        </TenantWriteRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/field-staff",
    element: (
      <ProtectedRoute>
        <RoleRoute allow={["superadmin"]}>
          <AppHeader title="Полевые сотрудники" onGoToUsers={() => {}} onLogout={() => {}} />
          <FieldStaffPage />
        </RoleRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/integrations",
    element: (
      <ProtectedRoute>
        <TenantWriteRoute>
          <AppHeader title="API-интеграции" onGoToUsers={() => {}} onLogout={() => {}} />
          <IntegrationsPage />
        </TenantWriteRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/lorawan/diagnostics",
    element: (
      <ProtectedRoute>
        <TenantWriteRoute>
          <AppHeader title="Диагностика LoRaWAN" onGoToUsers={() => {}} onLogout={() => {}} />
          <LoRaWanDiagnosticsPage />
        </TenantWriteRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/lorawan",
    element: (
      <ProtectedRoute>
        <RoleRoute allow={["superadmin"]}>
          <AppHeader title="BillingService LoRaWAN" onGoToUsers={() => {}} onLogout={() => {}} />
          <LoRaWanSettingsPage />
        </RoleRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/mobile-registrations",
    element: (
      <ProtectedRoute>
        <TenantWriteRoute>
          <AppHeader title="Регистрация в мобильном приложении" onGoToUsers={() => {}} onLogout={() => {}} />
          <MobileRegistrationsPage />
        </TenantWriteRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/mobile-news",
    element: (
      <ProtectedRoute>
        <TenantWriteRoute>
          <AppHeader title="Новости мобильного приложения" onGoToUsers={() => {}} onLogout={() => {}} />
          <MobileNewsPage />
        </TenantWriteRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/login",
    element: (
      <PublicRoute>
        <LoginPage />
      </PublicRoute>
    ),
  },
  {
    path: "*",
    element: <Navigate to="/home" replace />,
  },
  { path: "/terms", element: <TermsPage /> },
  { path: "/privacy", element: <PrivacyPolicyPage /> },
];

export default routes;
